import { BillingPlanDto } from '@shared/core/billing'
import { CalendarsLimitType } from '@shared/core/billing/calendars-limit-type'
import { format } from 'date-fns'
import { SubscriptionStatus } from 'src/modules/payment/types/payment.types'

export const isPlanNotForSale = (plan: BillingPlanDto): boolean => plan?.group_name === 'not_for_sale'

/**
 * Returns true when this profile is billed through Shopify (the user signed
 * up via the Shopify embedded app). Shopify-billed users see the same plans
 * and billing UI as Paddle users, but subscribe/upgrade/cancel actions are
 * routed to Shopify Admin.
 */
export const isShopifyBilledProfile = (profile?: { signup_source?: string | null } | null): boolean =>
  profile?.signup_source === 'shopify'

export type SubscriptionBillingProfile = {
  billing_cancellation_effective_date?: string | null
  signup_source?: string | null
  shopify_subscription_status?: string | null
  shopify_subscription_current_period_end?: string | null
  billing_paused_at?: Date | string | null
}

/** Access end for Paddle (`billing_cancellation_effective_date`, ms) or Shopify period end (ISO). */
export const subscriptionGracePeriodEndMs = (profile?: SubscriptionBillingProfile | null): number | null => {
  if (!profile) {
    return null
  }
  if (profile.signup_source === 'shopify') {
    if (!profile.shopify_subscription_current_period_end) {
      return null
    }
    const ms = new Date(profile.shopify_subscription_current_period_end).getTime()
    return Number.isFinite(ms) ? ms : null
  }
  if (profile.billing_cancellation_effective_date) {
    const n = parseInt(profile.billing_cancellation_effective_date, 10)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export const formatSubscriptionCancellationDisplay = (profile: SubscriptionBillingProfile): Date | null => {
  if (
    profile.signup_source === 'shopify' &&
    profile.shopify_subscription_current_period_end &&
    profile.shopify_subscription_status === 'cancelled'
  ) {
    const d = new Date(profile.shopify_subscription_current_period_end)
    if (!Number.isFinite(d.getTime())) {
      return null
    }
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  }
  if (!profile.billing_cancellation_effective_date) {
    return null
  }
  const cancellationDate = new Date(parseInt(profile.billing_cancellation_effective_date, 10))
  if (!Number.isFinite(cancellationDate.getTime())) {
    return null
  }
  return new Date(cancellationDate.getUTCFullYear(), cancellationDate.getUTCMonth(), cancellationDate.getUTCDate())
}

export const formatSubscriptionEndedOn = (profile: SubscriptionBillingProfile): string => {
  const endedOn = formatSubscriptionCancellationDisplay(profile)
  return endedOn ? format(endedOn, 'MMMM d, yyyy') : ''
}

export const parseBillingPausedAt = (profile: SubscriptionBillingProfile): Date | null => {
  if (!profile.billing_paused_at) {
    return null
  }
  const pausedAt = new Date(profile.billing_paused_at)
  return Number.isFinite(pausedAt.getTime()) ? pausedAt : null
}

export const isSubscriptionActive = (state: string, profile?: SubscriptionBillingProfile | null): boolean => {
  const endMs = subscriptionGracePeriodEndMs(profile)
  return (
    state === SubscriptionStatus.active ||
    state === SubscriptionStatus.trialing ||
    state === SubscriptionStatus.past_due ||
    (state === SubscriptionStatus.deleted && endMs != null && Date.now() < endMs)
  )
}

export const canShopifyUserChangePlanDuringGrace = (
  profile?: SubscriptionBillingProfile | null,
  state?: string | null,
): boolean =>
  isShopifyBilledProfile(profile) && state === SubscriptionStatus.deleted && isSubscriptionActive(state, profile)

export const getCalendarsLimitType = (plan: BillingPlanDto): CalendarsLimitType => {
  if (!plan) return null

  if (plan.calendars_limit === null && !plan.only_primary_calendar) {
    return CalendarsLimitType.unlimited
  }

  if (plan.only_primary_calendar) {
    return CalendarsLimitType.primary
  }

  return CalendarsLimitType.default
}
